import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Clock, LogIn, Timer, Wrench } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { useMaintenance } from '../context/MaintenanceContext';

function parseEstimatedEndToDate(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;

  const iso = new Date(raw);
  if (!Number.isNaN(iso.getTime())) return iso;

  const normalized = raw.replace(/\s+/g, ' ').toLowerCase();
  const match = normalized.match(
    /(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:\s*(?:ore)?\s*(\d{1,2}):(\d{2}))?/
  );
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  let year = Number(match[3]);
  const hour = match[4] != null ? Number(match[4]) : 0;
  const minute = match[5] != null ? Number(match[5]) : 0;

  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return null;
  if (year < 100) year += 2000;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (hour < 0 || hour > 23) return null;
  if (minute < 0 || minute > 59) return null;

  const d = new Date(year, month - 1, day, hour, minute, 0, 0);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function formatRemainingMs(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const totalHours = days * 24 + hours;
  const hh = String(totalHours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');

  return `${hh}:${mm}:${ss}`;
}

export default function MaintenancePage() {
  const { message, estimatedEnd } = useMaintenance();
  const targetDate = useMemo(() => parseEstimatedEndToDate(estimatedEnd), [estimatedEnd]);
  const [remainingMs, setRemainingMs] = useState(null);

  useEffect(() => {
    if (!targetDate) {
      setRemainingMs(null);
      return undefined;
    }
    const tick = () => {
      setRemainingMs(Math.max(0, targetDate.getTime() - Date.now()));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
        backgroundImage: (theme) =>
          `radial-gradient(1200px 600px at 50% -10%, ${alpha(theme.palette.info.main, 0.14)}, transparent 60%),
           radial-gradient(900px 500px at 80% 110%, ${alpha(theme.palette.warning.main, 0.12)}, transparent 55%)`,
      }}
    >
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          width: '100%',
          maxWidth: 560,
          p: 4,
          borderRadius: 3,
          textAlign: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '999px',
              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.18),
              color: 'warning.dark',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Wrench size={28} />
          </Box>
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
          Manutenzione in corso
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {message || 'Stiamo effettuando lavori di manutenzione. Torneremo online a breve.'}
        </Typography>

        {targetDate && remainingMs != null ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
              color: 'text.primary',
              mb: 1.5,
            }}
          >
            <Timer size={16} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Torna operativo tra:
            </Typography>
            <Typography variant="body2">{formatRemainingMs(remainingMs)}</Typography>
          </Box>
        ) : null}

        {estimatedEnd ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.06 : 0.12),
              color: 'text.primary',
              mb: 3,
            }}
          >
            <Clock size={16} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Fine stimata:
            </Typography>
            <Typography variant="body2">{estimatedEnd}</Typography>
          </Box>
        ) : null}

        <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Sei un admin?
          </Typography>
          <Button
            component={RouterLink}
            to="/login"
            variant="outlined"
            startIcon={<LogIn size={18} />}
            sx={{ textTransform: 'none' }}
          >
            Accedi come amministratore
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
